import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  LexicalComposer,
  InitialConfigType,
} from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $isElementNode,
} from 'lexical';
import NameDropdown from 'shiftNotes/components/NameDropdown';
import { ExtractedEntity } from 'shiftNotes/_types';
import { Profile } from 'teamProfiles/_types';
import StyledEditableContent from 'shiftNotes/styles/ShiftNote.style';
import { deduplicateAndFilterOverlapping } from 'shiftNotes/helpers/nameHighlighting';
import {
  MentionNode,
  $createMentionNode,
  $isMentionNode,
} from 'shiftNotes/lexical/MentionNode';
import MentionPlugin from 'shiftNotes/lexical/MentionPlugin';

interface EditableNoteContentProps {
  text: string;
  entities: ExtractedEntity[];
  profiles: Profile[];
  entityProfileChanges: Record<number, string>;
  onTextChange: (newText: string) => void;
  onProfileChange: (startPosition: number, newProfileId: string) => void;
  onViewModeProfileSelect?: (
    profile: Profile,
    entityExtractionId: string | undefined
  ) => void;
  onSave?: () => void;
  isEditing?: boolean;
  disabled?: boolean;
  noteId?: string;
  variant?: 'default' | 'compact';
}

function EditableSyncPlugin({ editable }: { editable: boolean }): null {
  const [editor] = useLexicalComposerContext();
  const wasEditableRef = useRef(editable);
  useEffect(() => {
    editor.setEditable(editable);
    if (editable && !wasEditableRef.current) {
      editor.focus();
    }
    wasEditableRef.current = editable;
  }, [editor, editable]);
  return null;
}

function Placeholder() {
  return <span style={{ opacity: 0.4 }}>Type your note…</span>;
}

function serializeEditor(editorState: any): string {
  let out = '';
  editorState.read(() => {
    const root = $getRoot();
    root.getChildren().forEach((node) => {
      if ($isElementNode(node)) {
        if (node.getType() === 'paragraph') {
          node.getChildren().forEach((child) => {
            if ($isMentionNode(child)) {
              out += child.getLabel();
            } else {
              out += child.getTextContent();
            }
          });
          out += '\n';
        } else if ($isMentionNode(node)) {
          out += node.getLabel();
        } else {
          out += node.getTextContent();
        }
      } else if ($isMentionNode(node)) {
        out += node.getLabel();
      } else {
        out += node.getTextContent();
      }
    });
  });
  return out.replace(/\n$/, '');
}

function buildInitialState(
  initialText: string,
  entities: ExtractedEntity[],
  profiles: Profile[],
  entityProfileChanges: Record<number, string>
) {
  return () => {
    const root = $getRoot();
    root.clear();
    const paragraph = $createParagraphNode();

    if (!entities || entities.length === 0) {
      paragraph.append($createTextNode(initialText));
      root.append(paragraph);
      return;
    }

    const personEntities = entities.filter(
      (e) => e.extraction_class_key.toLowerCase() === 'person'
    );
    const deduplicated = deduplicateAndFilterOverlapping(personEntities);
    const sorted = deduplicated;

    let cursor = 0;
    sorted.forEach((entity) => {
      const start = Math.max(0, entity.start_position);
      const end = Math.max(start, entity.end_position);
      if (end > initialText.length || end <= start) return;

      if (start > cursor) {
        paragraph.append($createTextNode(initialText.slice(cursor, start)));
      }

      const profileId =
        entityProfileChanges[entity.start_position] || entity.profile?.id;
      const profile = profileId
        ? profiles.find((p) => p.id === profileId)
        : undefined;
      const label = profile?.full_name || entity.extraction_text || '';
      if (label) {
        paragraph.append(
          $createMentionNode({
            id: profileId || `entity-${start}`,
            label,
            profileId: profileId || undefined,
            entityExtractionId: entity.id,
          })
        );
        const nextChar = initialText[end] || '';
        const needsSpace = nextChar !== '' && !/^[\s.,!?]/.test(nextChar);
        if (needsSpace) {
          paragraph.append($createTextNode(' '));
        }
      }
      cursor = end;
    });

    if (cursor < initialText.length) {
      paragraph.append($createTextNode(initialText.slice(cursor)));
    }

    root.append(paragraph);
  };
}

export default function EditableNoteContent({
  text,
  entities,
  profiles,
  entityProfileChanges,
  onTextChange,
  onViewModeProfileSelect,
  onSave,
  isEditing = false,
  disabled = false,
  noteId,
  variant = 'default',
}: EditableNoteContentProps): JSX.Element {
  const editable = !disabled && isEditing;
  const initialConfig: InitialConfigType = useMemo(
    () => ({
      namespace: 'shift-notes-editor',
      editable,
      onError(error) {
        console.error('Lexical error', error);
      },
      nodes: [MentionNode],
      editorState: buildInitialState(
        text,
        entities,
        profiles,
        entityProfileChanges
      ),
      theme: {
        paragraph: 'lex-paragraph',
      },
    }),
    [editable, text, entities, profiles, entityProfileChanges]
  );

  const isMobile = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const hasCoarsePointer = window.matchMedia?.('(pointer: coarse)').matches;
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const isMobileUserAgent = /Mobi|Android|iPhone|iPad|iPod/i.test(ua);
    return Boolean(hasCoarsePointer || isMobileUserAgent);
  }, []);

  const handleChange = useCallback(
    (editorState: any) => {
      const serialized = serializeEditor(editorState);
      if (serialized !== text) onTextChange(serialized);
    },
    [onTextChange, text]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!editable) return;
      if (event.key === 'Enter' && !event.shiftKey && !isMobile) {
        event.preventDefault();
        onSave?.();
      }
    },
    [editable, isMobile, onSave]
  );

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <EditableSyncPlugin editable={editable} />
      <StyledEditableContent $compact={variant === 'compact'}>
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              style={{ outline: 'none' }}
              onKeyDown={handleKeyDown}
            />
          }
          placeholder={<Placeholder />}
          ErrorBoundary={LexicalErrorBoundary as any}
        />
        <HistoryPlugin />
        <OnChangePlugin onChange={handleChange} />
        <MentionPlugin
          disabled={!editable}
          onViewModeProfileSelect={onViewModeProfileSelect}
          dropdownRenderer={(
            anchorRect,
            query,
            selectProfile,
            entityExtractionId,
            clickedMentionLabel,
            clickedProfileId
          ) => {
            const menuWidth = 298;
            const margin = 8;
            const top = anchorRect.bottom + 4;
            const left = Math.max(
              margin,
              Math.min(anchorRect.left, window.innerWidth - menuWidth - margin)
            );
            const hasLinkedProfile =
              !!clickedProfileId && !clickedProfileId.startsWith('entity-');
            return (
              <NameDropdown
                name={clickedMentionLabel || query}
                profiles={profiles}
                hiddenTrigger
                autoOpen
                hasProfile={hasLinkedProfile}
                initialProfileId={
                  hasLinkedProfile ? clickedProfileId : undefined
                }
                noteId={noteId}
                entityExtractionId={entityExtractionId}
                onSelectProfile={selectProfile}
                onClose={() => selectProfile(null)}
                anchorPosition={{ top, left }}
              />
            );
          }}
        />
      </StyledEditableContent>
    </LexicalComposer>
  );
}
